import React, { useContext, useEffect, useMemo, useState } from 'react'; import { useLocation, useHistory } from 'react-router-dom';
import { Grid } from 'react-bootstrap';
import qs from 'qs';
import { AppearanceContext } from '../../index';
import { getErrorMessage } from '../../../../utils/helper';
import AlertComponent from '../../../../components/Alert';
import PageNotFound from '../../../404';
import { routesAppearance } from '../../helpers';
import {
  languagesDelete,
  languagesMakeDefault,
  languagesReset,
  languagesSaveChanges,
  languagesTranslations,
} from '../../../../services/endpoints/languages';
import TranslationsList from './components/List';
import ModalsManager from './components/Modals/Manager';
import Loader from '../../../../components/Loaders/Loader';

const EditLanguage = () => {
  const location = useLocation();
  const history = useHistory();

  const { config } = useContext(AppearanceContext);

  const queryString = qs.parse(location.search.slice(1));
  const routeLanguages = useMemo(() => routesAppearance.find((route) => route.code === 'languages'), [])

  const [init, setInit] = useState(false);
  const [data, setData] = useState({});
  const [error, setError] = useState(null)
  const [tableError, setTableError] = useState(null)
  const [isLoading, setIsLoading] = useState({ page: true, table: false, modalSubmit: false });
  const [openModal, setOpenModal] = useState('');
  const [modalData, setModalData] = useState({});

  const { name, translations, can_delete, can_reset } = data;
  const isDefault = data.default;

  const fetchList = async () => {
    setIsLoading((prevState) => ({ ...prevState, table: true }));
    try {
      if (!queryString?.code) {
        throw new Error('code is empty')
      }
      const response = await languagesTranslations(queryString.code);
      if (response.success) {
        setData(response.data);
      }
      setIsLoading((prevState) => ({ ...prevState, table: false }));
      return response.data;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      setIsLoading((prevState) => ({ ...prevState, table: false }));
    }
  };

  const getAllData = async () => {
    setIsLoading((prevState) => ({ ...prevState, page: true }));

    try {
      if (config?.access?.languages_translations_list) {
        await fetchList(queryString);
        setIsLoading((prevState) => ({ ...prevState, page: false }));
      } else {
        setIsLoading((prevState) => ({ ...prevState, page: false }));
      }
      setInit(true);
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, page: false }));
    }
  }

  const onSaveChanges = async (name, messages) => {
    setIsLoading((prevState) => ({ ...prevState, table: true }));
    try {
      const response = await languagesSaveChanges(queryString.code, name, messages);
      if (response.success) {
        setTableError(null)
        fetchList()
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setTableError(errorMessage);
      setIsLoading((prevState) => ({ ...prevState, table: false }));
    }
  }

  const onMakeDefault = async () => {
    setIsLoading((prevState) => ({ ...prevState, table: true }));
    try {
      const response = await languagesMakeDefault(queryString.code);
      if (response.success) {
        setTableError(null)
        fetchList()
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setTableError(errorMessage);
      setIsLoading((prevState) => ({ ...prevState, table: false }));
    }
  }

  const onHideModals = () => {
    setOpenModal('');
    setModalData({});
  };

  const onConfirmReset = (e) => {
    e.preventDefault();
    setModalData({
      actionKey: 'reset',
      title: 'Reset changes',
      description: 'Are you sure you want to reset changes?',
      actionType: 'single',
      confirmBtnText: 'Reset',
      item: {},
    });
    setOpenModal('confirm');
  }
  const onConfirmDelete = (e) => {
    e.preventDefault();
    setModalData({
      actionKey: 'delete',
      title: 'Delete language',
      description: 'Are you sure you want to delete the language?',
      actionType: 'single',
      confirmBtnText: 'Delete',
      item: {},
    });
    setOpenModal('confirm');
  }

  const onReset = async () => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await languagesReset(queryString.code)
      onHideModals('');
      fetchList()
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  }

  const onDelete = async () => {
    setIsLoading((prevState) => ({ ...prevState, modalSubmit: true }));
    try {
      await languagesDelete(queryString.code)
      onHideModals('');
      history.push(routeLanguages.link)
    } catch (error) {
      setIsLoading((prevState) => ({ ...prevState, modalSubmit: false }));
    }
  }

  const onSubmitModal = async () => {
    switch (modalData?.actionType) {
    case 'single':
      if (modalData?.title === 'Reset changes') {
        await onReset()
      }
      if (modalData?.title === 'Delete language') {
        await onDelete()
      }
      break;

    default:
      break;
    }
  };

  useEffect(() => {
    getAllData()
    document.title = 'Edit language';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading.page && !init) {
    return <Loader />;
  }

  if (!config?.access?.languages_translations_list) {
    return <PageNotFound />
  }

  return (
    <Grid>
      <ModalsManager
        open={openModal}
        isLoading={isLoading.modal}
        isLoadingSubmit={isLoading.modalSubmit}
        modalData={modalData}
        values={[]}
        onSubmit={onSubmitModal}
        onHide={onHideModals}
      />
      {error
        ? <AlertComponent text={error} />
        : (
          <div>
            {tableError ? <AlertComponent text={tableError} /> : null}
            {config?.access?.languages_translations_list
              ? (
                <TranslationsList
                  can_reset={can_reset}
                  can_delete={can_delete}
                  isLoading={isLoading.table}
                  name={name}
                  translations={translations}
                  config={config}
                  isDefault={isDefault}
                  onSaveChanges={onSaveChanges}
                  onMakeDefault={onMakeDefault}
                  onReset={onConfirmReset}
                  onDelete={onConfirmDelete}
                />
              ) : null }
          </div>
        )}
    </Grid>
  );
};

export default EditLanguage;
